import { AI_SLOT_DEFINITIONS } from './aiSlots';
import { ICON_NAMES } from './icons';
import { SECTION_FIELDS, addRequestedParts, overflowItemGroups } from './templateZones';
import { localizeForm } from './templateFormText';
import { readSectionCopy } from './sectionCopyRead';
const COMPONENT_KINDS = new Set(['faq', 'reviews', 'logos', 'form', 'progress', 'countdown']);
const COMPONENT_FIELDS = ['title', 'label', 'expiredLabel', 'submit', 'success'];
const ENTRY_FIELDS = ['question', 'answer', 'name', 'meta', 'text', 'label'];
function normalizeComponents(raw) {
    if (!Array.isArray(raw))
        return [];
    return raw.slice(0, 12).flatMap((entry) => {
        if (!entry || typeof entry !== 'object')
            return [];
        const bag = entry;
        if (!COMPONENT_KINDS.has(bag.kind))
            return [];
        const out = { kind: bag.kind };
        for (const field of COMPONENT_FIELDS) {
            const value = text(bag[field], 200);
            if (value)
                out[field] = value;
        }
        if (Array.isArray(bag.entries)) {
            const entries = bag.entries.slice(0, MAX_ITEMS).flatMap((item) => {
                if (!item || typeof item !== 'object')
                    return [];
                const fields = item;
                const clean = {};
                for (const field of ENTRY_FIELDS) {
                    const value = text(fields[field], field === 'answer' || field === 'text' ? 600 : 160);
                    if (value)
                        clean[field] = value;
                }
                return Object.keys(clean).length ? [clean] : [];
            });
            if (entries.length)
                out.entries = entries;
        }
        return [out];
    });
}
const TEXT_FIELDS = ['eyebrow', 'headline', 'subheadline', 'body', 'primaryAction', 'secondaryAction'];
const ITEM_TEXT_FIELDS = ['title', 'body', 'author', 'role', 'value', 'label', 'price', 'period', 'question', 'answer'];
const MAX_ITEMS = 12;
const MAX_COLUMNS = 5;
const ICONS = new Set(ICON_NAMES);
function text(value, max) {
    if (typeof value !== 'string')
        return undefined;
    const clean = value.replace(/<[^>]*>/g, '').replace(/[<>]/g, '').replace(/\s+/g, ' ').trim();
    return clean ? clip(clean, max) : undefined;
}
/**
 * Lo que pasa del tope, sin cortar a media palabra (2026-11-03): un párrafo de
 * la IA salía publicado terminando en «se centra en la atención p». Se corta en
 * el final de la última frase que cabe y, si no hay ninguna que deje al menos
 * la mitad, en la última palabra entera con «…».
 */
export function clip(value, max) {
    if (value.length <= max)
        return value;
    const head = value.slice(0, max);
    const sentence = Math.max(head.lastIndexOf('. '), head.lastIndexOf('! '), head.lastIndexOf('? '), head.lastIndexOf('.'), head.lastIndexOf('!'), head.lastIndexOf('?'));
    if (sentence >= max / 2)
        return head.slice(0, sentence + 1).trimEnd();
    const space = head.lastIndexOf(' ');
    return `${(space >= max / 2 ? head.slice(0, space) : head.slice(0, max - 1)).replace(/[\s,;:]+$/, '')}…`;
}
/** Tolerante: lo que no encaja se descarta campo a campo, nunca la sección entera. */
export function normalizeSectionCopy(raw) {
    const source = (raw && typeof raw === 'object' ? raw : {});
    const copy = {};
    for (const field of TEXT_FIELDS) {
        /* El cuerpo de una sección «Nosotros» o de contenido es un párrafo largo de verdad. */
        const value = text(source[field], field === 'body' ? 900 : field === 'subheadline' ? 400 : 160);
        if (value)
            copy[field] = value;
    }
    if (Array.isArray(source.columns)) {
        const columns = source.columns.map((column) => text(column, 40)).filter((column) => Boolean(column)).slice(0, MAX_COLUMNS);
        if (columns.length)
            copy.columns = columns;
    }
    if (Array.isArray(source.items)) {
        const items = source.items.slice(0, MAX_ITEMS).flatMap((entry) => {
            if (!entry || typeof entry !== 'object')
                return [];
            const bag = entry;
            const item = {};
            for (const field of ITEM_TEXT_FIELDS) {
                const value = text(bag[field], field === 'body' || field === 'answer' ? 400 : 120);
                if (value)
                    item[field] = value;
            }
            /* Lo que dice una persona puede llegar como `quote` y quién lo dice como
               `name`: son la cita y el autor, no un campo de más que se tira. Sin
               esto la cita se perdía y en su lugar salía el cargo. */
            const quote = text(bag.quote, 400);
            if (quote && !item.body)
                item.body = quote;
            const name = text(bag.name, 120);
            if (name && !item.author)
                item.author = name;
            /* Y los nombres de siempre para lo de siempre: `name` o `heading` como
               título, `description`, `text` o `content` como cuerpo. Un elemento con
               todo escrito pero con otras llaves salía vacío. */
            /* El `name` de quien da una cita es su autor, no un título. */
            const person = Boolean(quote || bag.role);
            const heading = (person ? undefined : name) ?? text(bag.heading, 120);
            if (heading && !item.title)
                item.title = heading;
            const prose = text(bag.description, 400) ?? text(bag.text, 400) ?? text(bag.content, 400) ?? text(bag.summary, 400);
            if (prose && !item.body)
                item.body = prose;
            if (Array.isArray(bag.features)) {
                const features = bag.features.map((feature) => text(feature, 120)).filter((feature) => Boolean(feature)).slice(0, 8);
                if (features.length)
                    item.features = features;
            }
            if (typeof bag.icon === 'string' && ICONS.has(bag.icon))
                item.icon = bag.icon;
            return Object.keys(item).length ? [item] : [];
        });
        if (items.length)
            copy.items = items;
    }
    if (source.language === 'es' || source.language === 'en')
        copy.language = source.language;
    const components = normalizeComponents(source.components);
    if (components.length)
        copy.components = components;
    if (Array.isArray(source.must)) {
        const must = SECTION_FIELDS.filter((field) => source.must.includes(field));
        if (must.length)
            copy.must = must;
    }
    return copy;
}
/** Los roles que se marcan sin número y se cuentan por orden (`multiple`). */
const AUTO_NUMBERED = new Set(AI_SLOT_DEFINITIONS.filter((slot) => slot.multiple).map((slot) => slot.id));
const slotOf = (node) => (typeof node.aiSlot === 'string' ? node.aiSlot.trim() : '');
const isList = (slot) => slot === 'repeat.list' || slot === 'slot-list';
/** El valor de un elemento de la lista para un slot, sea cual sea su número. */
function itemValue(base, item, featureIndex) {
    switch (base) {
        /* `repeat.item.title`, `repeat.item.price` y `person-name`: los nombres que
           usan unos pocos bloques para lo mismo. Sin reconocerlos, su texto de
           muestra se quedaba —unos platillos en el sitio de un músico—. */
        case 'repeat.item.headline':
        case 'repeat.item.title':
        case 'slot-item-title':
        case 'headline':
        case 'label':
            return item.title ?? item.question ?? item.value ?? item.label;
        case 'repeat.item.body':
        case 'slot-item-body':
        case 'body':
        case 'subheadline':
        case 'quote':
            return item.body ?? item.answer ?? (item.value ? item.label : undefined);
        /* El pie de un elemento: el cargo de quien firma una cita. Tomaba el cuerpo,
           así que la cita salía dos veces —arriba y como firma—. */
        case 'caption':
            return item.role ?? item.label;
        case 'repeat.item.author':
        case 'slot-item-author':
        case 'author':
        case 'person-name':
            return item.author ?? item.title;
        case 'person-role':
            return item.role;
        case 'question':
            return item.question ?? item.title;
        case 'answer':
            return item.answer ?? item.body;
        case 'price.value':
        case 'repeat.item.price':
            return item.price ?? item.value;
        case 'price.period':
            return item.period;
        case 'price.feature':
            return item.features?.[featureIndex];
        case 'stat.value':
            return item.value ?? statFigure(item) ?? item.title;
        case 'stat.label':
            /* El título sólo si no hizo ya de cifra: con `{title: '+500'}` y sin
               `value`, la etiqueta repetía el número debajo de sí mismo. */
            return item.label ?? (item.value || statFigure(item) ? item.title : undefined) ?? item.body;
        default:
            return undefined;
    }
}
/**
 * La cifra de un resultado que llegó sin `value` y con el título en palabras.
 *
 * La IA a veces escribe `{title: 'Atletas transformados', body: 'Más de 500
 * miembros…'}`: el bloque de cifras pintaba «Atletas transformados» en grande,
 * donde va el número. Si el título no trae dígitos, se busca la cifra en la
 * descripción —«más de 500» → «500+», «el 95 %» → «95%»— o una década dicha en
 * palabras; el título pasa a ser la etiqueta. Sin nada que sacar, nulo (y se
 * queda como estaba).
 */
function statFigure(item) {
    if (!item.title || /\d/.test(item.title))
        return undefined;
    const body = item.body ?? '';
    const decade = /\b(una|a|one)\s+(d[ée]cada|decade)\b/i.test(body) ? '10+' : /\b(dos|two)\s+(d[ée]cadas|decades)\b/i.test(body) ? '20+' : undefined;
    const match = /(\d[\d.,]*)\s*(%|k\b|K\b|M\b|x\b|\+)?/.exec(body);
    if (!match)
        return decade;
    const figure = match[1].replace(/[.,]$/, '');
    const unit = match[2] === '%' ? '%' : match[2] && match[2] !== '+' ? match[2] : '';
    const more = match[2] === '+' || /(m[aá]s\s+de|over|more\s+than|\+)\s*$/i.test(body.slice(0, match.index));
    return `${figure}${unit}${more && !unit ? '+' : ''}`;
}
const ORDINAL = /^(\s*)(\d{1,3})([.)]?)(\s*)$/;
/**
 * Una cifra que en el template es un número de orden —el «01» de un paso—.
 *
 * `stat.value` sirve para las dos cosas: la cifra de un bloque de resultados
 * («+500») y el número de un paso («01»). Sin cifra escrita, la de resultados
 * toma el título (`{title: '+500'}`), pero en un paso el título es «Discovery»,
 * y acababa dentro del círculo del número. Aquí el número se conserva, con su
 * forma: `01`, `1.` o `1)`, renumerado por la posición del elemento.
 */
function ordinalFor(node, base, item, position) {
    /* `step.number` no tiene valor propio en el elemento: sin esto la tarjeta
       copiada se quedaba con el «1» de la primera («1, 1, 1, 1»). Y una cifra
       que la IA escribió como número de orden suelto («1») también se renumera. */
    const ordinalValue = !item.value || ORDINAL.test(item.value);
    if ((base !== 'stat.value' && base !== 'step.number') || !ordinalValue || typeof node.content !== 'string')
        return undefined;
    const match = ORDINAL.exec(node.content);
    if (!match)
        return undefined;
    return String(position).padStart(match[2].length, '0') + match[3];
}
const AUTHOR_BASES = new Set(['repeat.item.author', 'slot-item-author', 'author']);
/** Si algún nodo del árbol lleva un slot que cumpla la condición. */
function hasSlot(node, test) {
    const [base] = splitSlot(slotOf(node));
    return (Boolean(base) && test(base)) || node.children.some((child) => hasSlot(child, test));
}
/** `headline.3` → `['headline', 3]`; `eyebrow` → `['eyebrow', 0]`. */
function splitSlot(slot) {
    const match = /^(.*)\.(\d+)$/.exec(slot);
    return match ? [match[1], Number(match[2])] : [slot, 0];
}
/* Los nodos con texto que se escribieron en este reparto (ver `applySectionCopy`). */
let written = null;
const words = (value) => (value ?? '').replace(/<[^>]*>/g, ' ').trim().split(/\s+/).filter(Boolean).length;
/**
 * El texto del titular, o una frase entera si el bloque es una DECLARACIÓN:
 * su titular de muestra es una frase larga («We think good work should be
 * simple to buy…», 15 palabras), en letra grande y sola. Ahí un titular corto
 * —«Conócenos», «Tu primera visita incluye…»— dejaba el bloque casi vacío
 * (2026-10-29). Va el subtítulo o el primer párrafo, si dicen más y el bloque
 * no tiene ya dónde ponerlos.
 */
function statementText(node, copy, hasSubheadline, hasBody) {
    if (words(typeof node.content === 'string' ? node.content : '') < 10 || words(copy.headline) >= 8)
        return copy.headline;
    const firstParagraph = (copy.body ?? '').split(/\n\s*\n|(?<=[.!?])\s+/)[0]?.trim();
    const candidates = [hasSubheadline ? undefined : copy.subheadline, hasBody ? undefined : firstParagraph];
    return candidates.find((text) => words(text) >= 8) ?? copy.headline;
}
/**
 * La cita suelta de una sección (la de «Pull Quote with Text», 2026-10-30):
 * la IA no escribe un campo de cita para la sección, y el slot `quote` sólo se
 * llenaba con el texto de un elemento. Sin elementos se quedaba sin escribir y
 * se borraba, y el bloque perdía su columna izquierda. Va la frase más larga
 * que ya dice la sección: el subtítulo si no tiene dónde ir, si no la primera
 * frase del texto (una cita destacada repite el texto, como en una revista) y
 * al final el titular. Al menos 8 palabras si se puede: «Conócenos» no es una
 * cita. Con las comillas del template si las llevaba.
 */
function quoteText(node, copy, hasSubheadline, hasHeadline) {
    const sentences = (copy.body ?? '').split(/\n\s*\n|(?<=[.!?])\s+/).map((part) => part.trim()).filter(Boolean);
    const candidates = [hasSubheadline ? undefined : copy.subheadline, ...sentences.slice(0, 2), hasHeadline ? undefined : copy.headline, copy.subheadline, copy.headline];
    const chosen = candidates.find((text) => words(text) >= 8) ?? candidates.find((text) => words(text) > 0);
    if (!chosen)
        return undefined;
    const sample = typeof node.content === 'string' ? node.content.replace(/<[^>]*>/g, '').trim() : '';
    const quoted = /^["“«]/.test(sample) && !/^["“«]/.test(chosen);
    if (!quoted)
        return chosen;
    const [open, close] = sample.startsWith('«') ? ['«', '»'] : sample.startsWith('"') ? ['"', '"'] : ['“', '”'];
    return `${open}${chosen.replace(/[.]$/, '')}${close}`;
}
function setText(node, value) {
    if (!value)
        return;
    node.content = value;
    written?.add(node);
}
function setIcon(node, icon) {
    if (icon && ICONS.has(icon))
        node.iconName = icon;
}
/**
 * Dentro de un elemento de la lista: cada slot toma su valor del elemento.
 *
 * Las acciones no son del elemento sino de la sección: el botón de cada plan o
 * de cada producto dice lo que la IA escribió como acción de la sección. Sin
 * esto se quedaba el «Get started» del template en un sitio en español.
 */
/**
 * El número de paso de una tarjeta copiada. Una lista se rehace copiando su
 * primer elemento, y un número que no lleva slot (el «1» grande de un paso)
 * salía igual en todas: «1, 1, 1, 1». Un texto que sea sólo un número —«1»,
 * «01», «1.»— pasa a la posición de su tarjeta, con el mismo formato.
 */
const LONE_NUMBER = /^(\s*)(0*)(\d{1,2})(\.?\s*)$/;
function renumberCopy(node, position) {
    const visit = (current) => {
        if (!slotOf(current) && typeof current.content === 'string') {
            const match = LONE_NUMBER.exec(current.content);
            if (match) {
                const width = match[2].length + match[3].length;
                current.content = `${match[1]}${String(position).padStart(width, '0')}${match[4]}`;
            }
        }
        current.children.forEach(visit);
    };
    visit(node);
}
function fillItem(node, item, copy, position) {
    let feature = 0;
    /* La firma de un elemento sólo es el título cuando el título no se ve ya:
       en una tarjeta de proyecto con su titular, «autor = título» lo repetía
       debajo, con el retrato de muestra al lado. Sin autor, la firma se quita. */
    const showsTitle = hasSlot(node, (base) => base === 'headline' || base === 'repeat.item.headline' || base === 'slot-item-title');
    /* Un mismo texto no sale dos veces en la tarjeta: unos bloques llevan, además
       del título, etiquetas pequeñas que también leen el título, y salía
       «Garantía Garantía» encima de «Garantía». El título va primero a su sitio;
       lo que repetiría algo ya puesto se queda sin escribir (y se quita). */
    const used = new Set();
    const titled = new Set();
    const placeTitles = (current) => {
        const slot = slotOf(current);
        if (slot && TITLE_BASES.has(splitSlot(slot)[0])) {
            const value = itemValue(splitSlot(slot)[0], item, 0);
            if (value && !used.has(value)) {
                setText(current, value);
                used.add(value);
                titled.add(current);
            }
        }
        current.children.forEach(placeTitles);
    };
    placeTitles(node);
    const once = (value) => {
        if (!value)
            return value;
        if (used.has(value))
            return undefined;
        used.add(value);
        return value;
    };
    const visit = (current) => {
        const slot = slotOf(current);
        if (slot && !titled.has(current)) {
            const [base] = splitSlot(slot);
            if (base === 'repeat.item.icon' || base === 'icon')
                setIcon(current, item.icon);
            else if (base === 'action.primary' || base === 'repeat.item.action')
                setText(current, copy.primaryAction);
            else if (base === 'action.secondary' || base === 'action.link')
                setText(current, copy.secondaryAction ?? copy.primaryAction);
            else if (base === 'price.feature')
                setText(current, itemValue(base, item, feature++));
            else if (AUTHOR_BASES.has(base))
                setText(current, once(item.author ?? (showsTitle ? undefined : item.title)));
            else
                setText(current, ordinalFor(current, base, item, position) ?? once(itemValue(base, item, 0)));
        }
        current.children.forEach(visit);
    };
    visit(node);
}
/** Los huecos que son el título de una tarjeta: van primero (ver `fillItem`). */
const TITLE_BASES = new Set(['repeat.item.headline', 'repeat.item.title', 'slot-item-title', 'headline']);
/**
 * Los componentes que guardan su contenido en una lista propia y no en hijos
 * del lienzo —el acordeón de preguntas, las reseñas—. Sin esto la IA escribía
 * los textos, pero el componente seguía enseñando los de muestra: el reparto
 * sólo recorría elementos con slot, y estas listas no los tienen.
 *
 * Se reescribe la lista entera con un elemento por cada uno de la sección,
 * tomando del primero lo que no es texto (el marcador, la fuente, el avatar…).
 * Devuelve si lo hizo, para no repartir los mismos elementos dos veces.
 */
function fillBehaviorList(node, items) {
    const behavior = node.behavior;
    if (!behavior || !items.length)
        return false;
    if (behavior.type === 'faq') {
        const rows = items
            .map((item) => ({ question: item.question ?? item.title, answer: item.answer ?? item.body }))
            .filter((row) => row.question && row.answer);
        if (!rows.length)
            return false;
        /* Sólo pregunta y respuesta: una fila de muestra atada al CMS
           (`questionBinding`) no puede pasarle el enlace a las nuevas, y ninguna
           arranca abierta si nadie lo eligió. */
        node.behavior = {
            ...behavior,
            items: rows.slice(0, MAX_ITEMS).map((row, index) => ({ id: `faq-${index + 1}`, question: row.question, answer: row.answer })),
        };
        return true;
    }
    if (behavior.type === 'logos') {
        /* Logos: la IA sólo da nombres, que van como logos de texto. Nunca
           imágenes: un logo inventado en imagen pasaría por el de una marca real. */
        const names = items.map((item) => (item.title ?? item.author ?? '').trim()).filter(Boolean);
        if (!names.length)
            return false;
        node.behavior = { ...behavior, items: names.slice(0, MAX_ITEMS).map((text, index) => ({ id: `logo-${index + 1}`, kind: 'text', text })) };
        return true;
    }
    if (behavior.type === 'reviews') {
        const rows = items
            .map((item) => ({ name: item.author ?? item.title, meta: item.role, text: item.body }))
            .filter((row) => row.name && row.text);
        if (!rows.length)
            return false;
        const reviews = Array.isArray(behavior.reviews) ? behavior.reviews : [];
        node.behavior = {
            ...behavior,
            reviews: rows.slice(0, MAX_ITEMS).map((row, index) => {
                const model = reviews[index % Math.max(1, reviews.length)] ?? {};
                /* Ni la foto ni la fuente del modelo: son reseñas de ejemplo, y el
                   logo de Google o Facebook del template diría que salen de ahí. */
                const { meta: _meta, avatarUrl: _avatar, source: _source, sourceName: _sourceName, sourceLogoUrl: _sourceLogo, ...rest } = model;
                return { ...rest, id: `review-${index + 1}`, name: row.name, text: row.text, rating: 5, ...(row.meta ? { meta: row.meta } : {}) };
            }),
        };
        return true;
    }
    return false;
}
/**
 * Las pestañas van por parejas —la pestaña N abre el panel N—, así que con
 * menos elementos que pestañas sobran las dos cosas, no sólo su texto. Se
 * quitan las de más; el resto se rellena como cualquier otro slot.
 */
function trimTabs(node, count) {
    const behavior = node.behavior;
    if (behavior?.type !== 'tabs' || count < 1)
        return;
    const drop = (current) => {
        current.children = current.children.filter((child) => {
            const match = /^tabs-(?:tab|panel)-(\d+)$/.exec(typeof child.schemaId === 'string' ? child.schemaId : '');
            return !match || Number(match[1]) <= count;
        });
        current.children.forEach(drop);
    };
    drop(node);
}
/** Los slots de texto: los que, si la IA no los escribió, se quedan con el de muestra. */
const TEXT_SLOT_BASES = new Set([
    'eyebrow', 'slot-eyebrow', 'headline', 'slot-title', 'subheadline', 'slot-subtitle', 'body', 'slot-body',
    'caption', 'quote', 'author', 'label', 'question', 'answer', 'person-role',
    'stat.value', 'stat.label', 'price.value', 'price.period', 'price.feature', 'column',
    'action.secondary', 'action.link',
    /* Los datos de contacto de muestra —`hello@yourbusiness.com`, un `+1 (555)`—
       no los escribe la IA: si nadie los dio, fuera, con su icono. */
    'contact.email', 'contact.phone', 'contact.address',
    'repeat.item.headline', 'repeat.item.body', 'repeat.item.author', 'slot-item-title', 'slot-item-body', 'slot-item-author',
    'repeat.item.title', 'repeat.item.price', 'person-name',
]);
/** Lo que acompaña a un texto sin decir nada por sí solo: un icono o un número de orden. */
function isDecoration(node) {
    /* Unas estrellas sin su «4.9 de 300+ clientes» (cifra de muestra que se
       quita) se veían sueltas en el hero (2026-10-30): sin la cifra, fuera. */
    if (node.type === 'icon' || node.type === 'rating')
        return true;
    /* Un retrato sin el nombre de al lado tampoco dice nada: es la foto de
       muestra de una persona que no existe. */
    if (node.type === 'image' && splitSlot(slotOf(node))[0] === 'media.avatar')
        return true;
    /* Un número solo (el «2» de un paso, sin su paso): también el de su círculo. */
    const [base] = splitSlot(slotOf(node));
    if ((!base || base === 'step.number') && node.children.length === 0 && typeof node.content === 'string' && /^\s*\d{1,3}[.)]?\s*$/.test(node.content))
        return true;
    return node.type === 'div' && node.children.length > 0 && !node.behavior && !node.form && !node.src && node.children.every(isDecoration);
}
/** Un contenedor que sólo agrupaba lo que se quitó: sin hijos ni nada propio que pintar. */
function isEmptyGroup(node) {
    return node.type === 'div' && node.children.length === 0 && !node.behavior && !node.form && !node.src
        && !(typeof node.content === 'string' && node.content.trim());
}
/**
 * Lo que quedó con el texto de muestra del template, fuera.
 *
 * Los templates están escritos en inglés neutro («By the numbers», «Choose
 * plan», «One clear sentence about…»). Si la IA no escribió ese campo —no puso
 * antetítulo, dio tres cifras para un bloque de cuatro—, el texto de muestra se
 * quedaba en un sitio en español. Un antetítulo de menos no se nota; uno en
 * otro idioma, o una instrucción para quien arma la plantilla, sí. Se quitan, y
 * con ellos los grupos que se quedan vacíos (la celda de la cuarta cifra).
 * La acción principal no se toca: un bloque de llamada a la acción sin botón
 * perdería su razón de ser.
 */
function stripUnwritten(nodes, done) {
    const clean = (list) => list.flatMap((node) => {
        const [base] = splitSlot(slotOf(node));
        /* Lo que ya marcó `markContactSlots` (un dato de contacto, real o «por
           completar») no es texto de muestra: se queda. */
        if (base && TEXT_SLOT_BASES.has(base) && !done.has(node) && !node.contactSlot && typeof node.content === 'string')
            return [];
        const before = node.children.length;
        node.children = clean(node.children);
        /* Y un grupo que se quedó sólo con adornos —el check de un punto, el «04»
           de un paso que no se escribió— tampoco pinta nada. */
        const lost = node.children.length < before;
        const onlyDecor = lost && node.children.length > 0 && node.children.every(isDecoration);
        return (before > 0 && isEmptyGroup(node)) || onlyDecor ? [] : [node];
    });
    for (const node of nodes)
        node.children = clean(node.children);
}
const REPEAT_COLUMNS = /^repeat\(\s*(\d+)\s*,\s*(.+)\)$/;
/**
 * Una rejilla que perdió columnas se queda con las que tiene.
 *
 * Un bloque de cuatro pasos con la IA escribiendo tres dejaba la rejilla en
 * `repeat(4, 1fr)` y un hueco donde iba el cuarto. Sólo se tocan las rejillas
 * a las que se les quitó o añadió hijos al escribir los textos, y sólo si sus
 * columnas son iguales (`repeat(4, 1fr)` o `1fr 1fr 1fr 1fr`): una rejilla
 * con columnas de anchos distintos es un diseño, no una lista.
 */
function fitColumns(value, count) {
    if (typeof value !== 'string' || count < 1)
        return undefined;
    const trimmed = value.trim();
    const repeat = REPEAT_COLUMNS.exec(trimmed);
    if (repeat) {
        const columns = Number(repeat[1]);
        return columns > count ? `repeat(${count}, ${repeat[2].trim()})` : undefined;
    }
    const tracks = trimmed.split(/\s+/);
    if (tracks.length > count && tracks.every((track) => track === tracks[0]) && !trimmed.includes('(')) {
        return Array.from({ length: count }, () => tracks[0]).join(' ');
    }
    return undefined;
}
function fitGrids(nodes, before) {
    const visit = (node) => {
        const had = before.get(node);
        const count = node.children.length;
        /* Y al revés: una fila de tres pensada para tres que recibe cuatro pasa a
           cuatro columnas —hasta cuatro; más, mejor en dos filas—. Sólo si sus
           columnas eran una por elemento: ahí la fila era la lista entera. */
        if (had !== undefined && count > had && count <= 4) {
            const styles = node.styles;
            const desktop = styles?.desktop;
            const repeat = typeof desktop?.gridTemplateColumns === 'string' ? REPEAT_COLUMNS.exec(desktop.gridTemplateColumns.trim()) : null;
            if (desktop && repeat && Number(repeat[1]) === had)
                desktop.gridTemplateColumns = `repeat(${count}, ${repeat[2].trim()})`;
        }
        if (had !== undefined && count > 0 && count < had) {
            const styles = node.styles;
            for (const bag of Object.values(styles ?? {})) {
                if (!bag)
                    continue;
                const next = fitColumns(bag.gridTemplateColumns, count);
                if (next)
                    bag.gridTemplateColumns = next;
            }
        }
        node.children.forEach(visit);
    };
    nodes.forEach(visit);
}
/**
 * Escribe los textos de una sección en un template. Las listas repetibles se
 * rehacen con un elemento por cada uno que trae la sección, copiando el
 * primero; los slots numerados (`headline.2`, `stat.value.3`…) toman el
 * elemento que les toca. Lo que no tiene texto se queda como estaba.
 *
 * Un rol sin número (`body`, `stat.value`…) se numera aquí, por orden de
 * aparición en el bloque: el primer «Párrafo» es `body.1`, el siguiente
 * `body.2`. Así quien arma el template no tiene que numerar a mano.
 */
export function applySectionCopy(elements, copy, cloneNode) {
    const items = copy.items ?? [];
    /* Sólo con textos de verdad se limpian los de muestra: sin nada escrito
       (una sección sin copy), el template se queda tal cual. */
    const hasCopy = Boolean(copy.headline || copy.subheadline || copy.body || items.length);
    const done = new WeakSet();
    written = done;
    /* Primero lo que se pidió y el template no trae, con su texto: luego el
       reparto lo encuentra por su slot como a cualquier otro. */
    for (const node of addRequestedParts(elements, copy, cloneNode))
        done.add(node);
    /* Cuántos hijos tenía cada nodo antes de escribir: para ajustar después las
       rejillas que se quedan con menos (`fitGrids`). */
    const before = new WeakMap();
    const count = (node) => { before.set(node, node.children.length); node.children.forEach(count); };
    elements.forEach(count);
    /* Los elementos que siguen fuera de la lista (`overflowItemGroups`): la
       lista toma los que caben en ella y estos, los siguientes. */
    const overflow = items.length ? overflowItemGroups(elements) : null;
    const listTake = overflow ? Math.min(items.length, overflow.list.children.length) : MAX_ITEMS;
    const overflowItem = new Map(overflow ? overflow.groups.map((group, index) => [group, listTake + index]) : []);
    const unused = new Set();
    let featureCursor = 0;
    const flatFeatures = items.flatMap((item) => item.features ?? []);
    const seen = new Map();
    let behaviorFilled = false;
    /* Varias listas de preguntas en un bloque (una por columna): las de la IA se
       reparten entre ellas por partes iguales. Antes la primera se llevaba todas
       y la segunda se quedaba con sus preguntas de muestra (2026-10-30). Si no
       dan parejo, sobra la última: las columnas quedan iguales. */
    const faqLists = [];
    const findFaqs = (node) => {
        if (node.behavior?.type === 'faq')
            faqLists.push(node);
        node.children.forEach(findFaqs);
    };
    elements.forEach(findFaqs);
    const faqShare = new Map();
    if (faqLists.length > 1 && items.length) {
        const each = Math.max(1, Math.floor(items.length / faqLists.length));
        faqLists.forEach((list, index) => faqShare.set(list, items.slice(index * each, (index + 1) * each)));
    }
    const countSlot = (name) => {
        let found = 0;
        const look = (node) => { if (splitSlot(slotOf(node))[0] === name)
            found += 1; node.children.forEach(look); };
        elements.forEach(look);
        return found;
    };
    const hasSubheadline = countSlot('subheadline') > 0;
    const hasItemList = items.length > 0 && elements.some(function holds(node) { return isList(slotOf(node)) || node.children.some(holds); });
    const hasAuthors = ['repeat.item.author', 'slot-item-author', 'author', 'person-name'].some((name) => countSlot(name) > 0);
    /* Sólo en un bloque con un único párrafo (el de un producto): donde hay
       varios, el primero es la entrada y los demás son de los elementos, y la
       descripción del primero saldría dos veces. */
    const singleBody = countSlot('body') === 1;
    /* Un texto de varios párrafos (los de «Nosotros», separados por una línea en
       blanco) en un bloque con varios párrafos: uno por párrafo. Antes el segundo
       y el tercero buscaban elementos y salían vacíos (o con un valor). */
    const paragraphs = countSlot('body') > 1 ? (copy.body ?? '').split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean) : [];
    const visit = (node) => {
        if (copy.language && node.form && typeof node.form === 'object')
            node.form = localizeForm(node.form, copy.language);
        trimTabs(node, items.length);
        const share = faqShare.get(node);
        if (share) {
            if (!fillBehaviorList(node, share))
                node.behavior = { ...node.behavior, items: [] };
            return;
        }
        if (!behaviorFilled && fillBehaviorList(node, items)) {
            behaviorFilled = true;
            return;
        }
        const at = overflowItem.get(node);
        if (at !== undefined) {
            const item = items[at];
            if (item)
                fillItem(node, item, copy, at + 1);
            else
                unused.add(node);
            return;
        }
        const slot = slotOf(node);
        if (slot && isList(slot)) {
            const prototype = node.children[0];
            if (prototype && items.length) {
                /* Las cifras, como mucho tantas como trae el diseño: su rejilla está
                   hecha para ese número (4 en dos filas de 2, 3 en una de 3) y una de
                   más o de menos deja un hueco. */
                const designed = hasSlot(prototype, (base) => base === 'stat.value') ? node.children.length : 8;
                node.children = items.slice(0, Math.min(8, listTake, Math.max(designed, 1))).map((item, index) => {
                    const copyNode = cloneNode(prototype);
                    fillItem(copyNode, item, copy, index + 1);
                    renumberCopy(copyNode, index + 1);
                    return copyNode;
                });
            }
            return;
        }
        if (slot) {
            const [base, numberedAt] = splitSlot(slot);
            const n = numberedAt === 0 && AUTO_NUMBERED.has(base) ? (seen.set(base, (seen.get(base) ?? 0) + 1).get(base)) : numberedAt;
            switch (base) {
                case 'eyebrow':
                case 'slot-eyebrow':
                    setText(node, copy.eyebrow);
                    break;
                case 'slot-title':
                    setText(node, copy.headline);
                    break;
                case 'slot-subtitle':
                    setText(node, copy.subheadline);
                    break;
                case 'slot-body':
                    setText(node, copy.body);
                    break;
                case 'slot-cta':
                case 'navigation.cta':
                    setText(node, copy.primaryAction);
                    break;
                case 'action.primary':
                    setText(node, copy.primaryAction);
                    break;
                case 'action.secondary':
                    setText(node, copy.secondaryAction);
                    break;
                case 'action.link':
                    setText(node, copy.secondaryAction);
                    break;
                case 'headline':
                    /* Un título suelto en un bloque con su lista (el «Qué pasa después»
                       encima de los pasos): es el de la lista, no un elemento. Antes se
                       llevaba el título del primero, que salía dos veces, o se quitaba. */
                    if (n >= 2 && hasItemList) {
                        written?.add(node);
                        break;
                    }
                    setText(node, n <= 1 ? statementText(node, copy, hasSubheadline, countSlot('body') > 0) : items[n - 2] && itemValue(base, items[n - 2], 0));
                    break;
                case 'subheadline':
                    setText(node, n <= 1 ? copy.subheadline : items[n - 2] && itemValue(base, items[n - 2], 0));
                    break;
                case 'body':
                    /* Los párrafos primero; un hueco más allá de ellos sigue como antes
                       (el texto de un elemento). Los valores van en su lista. */
                    if (paragraphs.length > 1 && n <= paragraphs.length) {
                        setText(node, paragraphs[n - 1]);
                        break;
                    }
                    /* Sin cuerpo propio, el primer párrafo dice lo del subtítulo —o, en
                       un bloque de un solo producto, su descripción—: mejor que vacío. */
                    setText(node, n <= 1 ? copy.body ?? (hasSubheadline ? undefined : copy.subheadline) ?? (singleBody ? items[0]?.body : undefined) : items[n - 2] && itemValue(base, items[n - 2], 0));
                    break;
                case 'quote':
                    /* Una cita de la sección (sin firma al lado): de lo que dice la
                       sección. Con firmas, son las opiniones de los elementos. */
                    if (n <= 1 && !hasAuthors) {
                        setText(node, quoteText(node, copy, hasSubheadline, countSlot('headline') > 0));
                        break;
                    }
                    if (n >= 1 && items[n - 1])
                        setText(node, itemValue(base, items[n - 1], 0));
                    break;
                case 'price.feature':
                    /* Sin puntos escritos, los títulos de los elementos: en un bloque de
                       un producto, sus variantes o lo que incluye. */
                    setText(node, flatFeatures.length ? flatFeatures[featureCursor++] : items[featureCursor++]?.title);
                    break;
                case 'column':
                    setText(node, copy.columns?.[n - 1]);
                    break;
                case 'icon':
                    setIcon(node, items[n - 1]?.icon);
                    break;
                default:
                    if (n >= 1 && items[n - 1])
                        setText(node, ordinalFor(node, base, items[n - 1], n) ?? itemValue(base, items[n - 1], 0));
            }
        }
        node.children.forEach(visit);
    };
    try {
        elements.forEach(visit);
    }
    finally {
        written = null;
    }
    /* Los de fuera que se quedaron sin elemento, fuera, y con ellos la columna
       que los agrupaba si se queda vacía. */
    if (unused.size) {
        const prune = (list) => list.flatMap((node) => {
            if (unused.has(node))
                return [];
            const had = node.children.length;
            node.children = prune(node.children);
            return had > 0 && node.children.length === 0 && isEmptyGroup(node) ? [] : [node];
        });
        for (const root of elements)
            root.children = prune(root.children);
    }
    if (hasCopy)
        stripUnwritten(elements, done);
    fitGrids(elements, before);
    keepSpareItems(elements, items);
}
/**
 * Los elementos que el diseño no enseña, guardados en la sección (2026-11-04,
 * él lo pidió): la IA escribe 4 opiniones y un diseño de una sola tarjeta
 * enseñaba una y tiraba las otras; al cambiar a uno de tres, salía una. Ahora
 * se quedan en `spareItems` de la sección y «Cambiar diseño» los vuelve a
 * repartir (ver `withSpareItems`). Nada de esto se dibuja.
 */
function keepSpareItems(elements, items) {
    const root = elements[0];
    if (!root)
        return;
    const shown = items.length ? readSectionCopy(elements).copy.items?.length ?? 0 : 0;
    if (items.length > shown)
        root.spareItems = items.slice(shown).map((item) => ({ ...item, ...(item.features ? { features: [...item.features] } : {}) }));
    else
        delete root.spareItems;
}
const itemKey = (item) => JSON.stringify([item.title ?? '', item.body ?? '', item.question ?? '', item.author ?? '', item.value ?? '']);
/** Lo que se ve de la sección y, detrás, lo guardado que no cabía en su diseño (sin repetir). */
export function withSpareItems(shown, root) {
    const spare = Array.isArray(root?.spareItems) ? root.spareItems.filter((item) => item && typeof item === 'object') : [];
    if (!spare.length)
        return [...shown];
    const seen = new Set(shown.map(itemKey));
    return [...shown, ...spare.filter((item) => !seen.has(itemKey(item)))];
}
//# sourceMappingURL=templateCopy.js.map